import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PATHS } from 'src/constants/navigateRoutes';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';

/**
 * Converts the retired revamp questionnaire into a durable, server-governed
 * completion only after policy routing has finished and adult consent exists.
 * It renders nothing and leaves every legacy tenant on the original wizard.
 */
const RevampOnboardingBootstrap = observer(() => {
  const {
    completeRevampOnboarding,
    getOnBoardingCompleteStatus,
    getUserDetails,
    isRevampExperience,
  } = useAuthStore();
  const { notify } = useAlertStore();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUserDetails();
  const revampExperience = isRevampExperience();

  useEffect(() => {
    const userId = user?._id?.toString();
    const organizationId = user?.organizationId?.toString();

    if (
      !revampExperience ||
      !userId ||
      !organizationId ||
      user?.adultConsentConfirmed !== true
    ) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const isComplete = await getOnBoardingCompleteStatus();

      if (cancelled || isComplete === true) {
        return;
      }

      if (isComplete !== false) {
        notify(
          'Hana could not verify workspace setup. Refresh to try again.',
          'error',
        );

        return;
      }

      const completed = await completeRevampOnboarding();

      if (cancelled) {
        return;
      }

      if (!completed) {
        notify(
          'Hana could not finish secure workspace setup. Refresh to try again.',
          'error',
        );

        return;
      }

      if (location.pathname !== PATHS.getStarted) {
        navigate(PATHS.getStarted, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    completeRevampOnboarding,
    getOnBoardingCompleteStatus,
    location.pathname,
    navigate,
    notify,
    revampExperience,
    user?._id,
    user?.adultConsentConfirmed,
    user?.organizationId,
  ]);

  return null;
});

export default RevampOnboardingBootstrap;
